<template>
  <div class="page-form-wrap">
    <div class="page-form-cancel">
      <b-btn
        v-if="isAdmin && !report.assigneeId && isAssignable"
        class="mr-3"
        variant="success"
        @click="assignReport">
        Assign to Me
      </b-btn>
      <b-btn class="page-cancel-button"
             variant="link"
             type="submit"
             to="/reports">
        <svg-icon name="close" /> Cancel
      </b-btn>
    </div>
    <div class="page-form-header d-flex justify-content-between align-items-center">
      <h1>
        Order #{{ report.id }}
        <span class="report-type ml-3" :style="{ backgroundColor: report.labelColor }">
          <span v-if="isFollowUp && !isCondensed">Follow-up </span>
          <span v-if="!isFollowUp && isCondensed">Condensed </span>
          <span v-if="!isFollowUp && !isCondensed" class="text-white">Initial </span>
          <span class="text-white">{{ report.labelTypeText }} </span>
          <span>{{ report.labelSubTypeText }}</span>
        </span>
      </h1>
      <div class="d-flex" v-if="isAdmin" >
        <b-button v-if="canExport" class="report-edit mr-3" variant="success" @click="exportDataFeed()">Export Data Feed</b-button>
        <b-button class="report-edit mr-3" variant="success" @click="editReportTag">Edit Tag</b-button>
        <b-button class="report-edit" variant="success" @click="editReport">Edit</b-button>
      </div>
    </div>
    <div class="page-form-content">
      <div class="row form-order-info">
        <div class="col-12 col-lg-6 order-lg-last">
          <b-form-group>
            <div>Status</div>
            <strong><report-status-text :status="report.status" :labworkType="report.labworkType" /></strong>
          </b-form-group>
          <b-form-group :class="{ 'client-draw': report.labworkType === labworkType.ClientDraw }">
            <div>Labwork Type</div>
            <strong>{{ report.labworkTypeName | userData }}</strong>
          </b-form-group>
          <b-form-group :class="{ 'highlight': report.expectedSampleDate }">
            <div>Expected Sample Collection Date</div>
            <strong>{{ report.expectedSampleDate ? formatDate(report.expectedSampleDate) : "No info" }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Provider First Name</div>
            <strong>{{ employee.firstName | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Provider Last Name</div>
            <strong>{{ employee.lastName | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Provider NPI</div>
            <strong>{{ employee.physicianNPI | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Client</div>
            <strong>{{ report.client.name | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Email</div>
            <strong>{{ report.patientEmail | userData }}</strong>
          </b-form-group>
        </div>

        <div class="col-12 col-lg-6 order-lg-first">
          <b-form-group v-if="isAdmin">
            <div>Assignee</div>
            <strong>{{ report.assigneeName | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Name</div>
            <strong>{{ report.patientName | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Date of Birth</div>
            <strong>{{ report.patientBirthDate | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Gender</div>
            <strong>{{ report.patientGender | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Patient ID / MRN</div>
            <strong>{{ report.patientMRN | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Address</div>
            <strong>{{ (report.survey.address && report.survey.address.address1) | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Address 2</div>
            <strong>{{ (report.survey.address && report.survey.address.address2) | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Zip</div>
            <strong>{{ (report.survey.address && report.survey.address.zip) | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>City</div>
            <strong>{{ (report.survey.address && report.survey.address.city) | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>State</div>
            <strong>{{ (report.survey.address && report.survey.address.state) | userData }}</strong>
          </b-form-group>
          <b-form-group>
            <div>Phone</div>
            <strong>{{ report.patientPhone | userData }}</strong>
          </b-form-group>
        </div>
      </div>
      <b-form @submit.prevent="submit">
        <div class="row">
          <div class="col-12 col-md-6 col-lg-6 col-xl-6">
            <validation-summary :errors="errors"></validation-summary>
            <v-form-group :validator="$v.report.labcorpNumber"
                          label="LabCorp Requisition Number">
              <b-form-input :disabled="report.status == reportStatus.Completed || report.assigneeId != $auth.id"
                            slot-scope="{ attrs, listeners }"
                            v-bind="attrs"
                            v-on="listeners"
                            v-model="report.labcorpNumber"
                            placeholder="LabCorp Requisition Number" />
            </v-form-group>
          </div>
          <div class="col-12 col-md-6 col-lg-6 col-xl-6 row flex-nowrap align-items-center">
            <upload-file class="ml-3 ml-md-0"
                         :url="`/server/report/${reportId}/upload/req-document`"
                         :disabled="isUploadDisabled"
                         @file-uploaded="reqDocumentUploaded"
                         upload-text="Upload Req" />
            <b-btn class="text-nowrap" v-if="report.hasReqDocument" variant="link" @click="downloadReqDocument()">Download lab requisition document</b-btn>
          </div>
        </div>
        <b-form-group class="form-buttons" v-if="isAdmin && report.assigneeId == $auth.id">
          <b-btn variant="success" type="submit" :disabled="report.status == reportStatus.Completed">Save</b-btn>
        </b-form-group>
      </b-form>
    </div>
    <report-edit-modal ref="reportEditModal" @success="reportEdited"></report-edit-modal>
    <report-tag-edit-modal ref="reportTagEditModal" @success="reportEdited" :clientId="report.clientId" />
  </div>
</template>

<script>
  import { required } from "vuelidate/lib/validators";
  import formMixin from "mixins/form-mixin";

  import moment from "moment";

  import { createNamespacedHelpers } from "vuex";
  const { mapState, mapActions } = createNamespacedHelpers("report");

  import reportService from "services/report-service";

  import reportStatusText from "./report-status-text";
  import reportEditModal from "./report-edit-modal";
  import reportTagEditModal from "components/common/modals/report-tag-create-edit-modal";

  import reportStatus from "enums/report-status";
  import productType from "enums/product-type";
  import labworkType from "enums/labwork-type";

  export default {
    mixins: [formMixin],

    components: {
      reportStatusText,
      reportEditModal,
      reportTagEditModal
    },

    data() {
      return {
        reportStatus,
        labworkType
      }
    },

    validations: {
      report: {
        labcorpNumber: {
          required
        }
      }
    },

    computed: {
      ...mapState(["report"]),

      reportId() { return this.$route.params.id },

      employee() {
        return !this.report.employee ? '' : this.report.employee;
      },

      isAdmin() {
        return this.$auth.isInRole("Admin");
      },

      canExport() {
        return this.report.status === reportStatus.Completed &&
          (this.report.typeId === productType.InitialCRAAPOB ||
          this.report.typeId === productType.RepeatedCRAAPOB);
      },

      isUploadDisabled() {
        let disabled = false;

        if (
          this.status == reportStatus.Completed ||
          this.report.assigneeId != this.$auth.id
        )
          disabled = true;

        if (
          (this.report.status === reportStatus.OrderLabsFirst ||
          this.report.status === reportStatus.FinalProcessing ||
          this.report.status === reportStatus.WaitingResults) &&
          this.isAdmin &&
          this.report.assigneeId == this.$auth.id
        )
          disabled = false;

        return disabled;
      },

      isFollowUp() {
        return [
          productType.RepeatedCRAAPOB,
          productType.RepeatedCRALDLP,
          productType.RepeatedDRA,
          productType.RepeatedCRAHOMAIR
        ].includes(this.report.typeId);
      },

      isCondensed() {
        return this.report.typeId === productType.CondensedRepeatedCRAAPOB;
      },

      isAssignable() {
        return [
          reportStatus.WaitingReview,
          reportStatus.WaitingResults,
          reportStatus.WaitingSurvey,
          reportStatus.OrderLabsFirst,
          reportStatus.LabsWaitingSurvey,
          reportStatus.OrderOnHold,
          reportStatus.FinalProcessing
        ].includes(this.report.status);
      },
    },

    methods: {
      ...mapActions(["getById", "updateReport"]),

      async onSubmit() {
        const result = await reportService.saveLabcorpNumber(this.report.id, this.report.labcorpNumber);
        this.updateReport(result.report);
        this.$root.successToast("Labcorp number saved!");

        if (result.nextReportId) {
          await this.getById(result.nextReportId);
          this.$v.$reset();
          window.scrollTo(0, 0);
          this.$router.push(`../${result.nextReportId}/view`);
        }
      },

      editReport() {
        this.$refs.reportEditModal.show(this.reportId);
      },

      editReportTag() {
        this.$refs.reportTagEditModal.show(this.reportId);
      },

      async reportEdited() {
        this.$root.successToast("Report updated!");

        await this.getById(this.reportId)
      },

      async exportDataFeed() {
        await reportService.exportDataFeed(this.report);
        this.$root.successToast("Data feed downloaded!");
      },

      reqDocumentUploaded() {
        this.$root.successToast("Requisition document uploaded!");
        this.report.hasReqDocument = true;
      },

      async downloadReqDocument() {
        await reportService.downloadReqDocument(this.report);
      },

      formatDate(date) {
        return moment(date).format('MM/DD/YYYY');
      },

    async assignReport() {
      await reportService.assign(this.reportId);
      this.$root.successToast("Report assigned!");
      await this.getById(this.reportId);
    },
  },

    async created() {
      await this.getById(this.reportId);
    }
  };
</script>

<style lang="scss" scoped>
  .client-draw, .highlight {
    div {
      margin: 0 0 .25rem 0;
    }

    strong {
      margin: 0 0 0 -.5rem;
      padding: .25rem .5rem;
      border-radius: 1rem;
      background: #ffc107;
    }
  }
</style>